import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Copy, Trash2, Clock, Eye, Lock, FileText, Files as FilesIcon, Link2 as Link2Icon, ArrowRight, Inbox, Check, ShieldAlert, Palette, Globe, CreditCard, Building2, Activity, Filter } from 'lucide-react';
import { listMyPushes, deletePush, listBrandPushes, getBrandStats } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s fa`;
  if (s < 3600) return `${Math.floor(s / 60)}m fa`;
  if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
  return `${Math.floor(s / 86400)}g fa`;
}

function kindMeta(k) {
  if (k === 'text') return { label: 'Testo', icon: FileText, color: 'text-[#0d63b0] bg-blue-50 border-blue-100' };
  if (k === 'files') return { label: 'File', icon: FilesIcon, color: 'text-emerald-700 bg-emerald-50 border-emerald-100' };
  return { label: 'URL', icon: Link2Icon, color: 'text-amber-700 bg-amber-50 border-amber-100' };
}

export default function Dashboard() {
  const { user, brand, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [filter, setFilter] = useState('all'); // all | active | expired

  const isReseller = !!user;

  const load = async () => {
    try {
      const d = isReseller ? await listBrandPushes() : await listMyPushes();
      setItems(d);
    } catch { setItems([]); }
    if (isReseller) {
      try { setStats(await getBrandStats()); } catch {}
    }
  };

  useEffect(() => { if (!authLoading) load(); /* eslint-disable-next-line */ }, [authLoading, isReseller]);

  const copy = async (t, slug) => {
    try {
      const url = slug ? `${window.location.origin}/b/${slug}/p/${t}` : `${window.location.origin}/p/${t}`;
      await navigator.clipboard.writeText(url);
      setCopiedToken(t);
      toast.success('Link copiato');
      setTimeout(() => setCopiedToken(null), 1800);
    } catch { toast.error('Errore'); }
  };

  const remove = async (t) => {
    if (!confirm('Eliminare definitivamente questo push?')) return;
    try { await deletePush(t); toast.success('Push eliminato'); load(); }
    catch { toast.error('Errore'); }
  };

  const filtered = items?.filter((p) => {
    if (filter === 'active') return !p.expired;
    if (filter === 'expired') return p.expired;
    return true;
  });

  return (
    <div className="py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0d63b0]">
              {isReseller ? `Pannello Reseller · ${brand?.name}` : 'Dashboard sessione'}
            </div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">
              {isReseller ? 'I tuoi push' : 'I miei push'}
            </h1>
            <p className="mt-1 text-slate-600 text-sm">
              {isReseller
                ? 'Tutti i push generati dal tuo account, indipendentemente dal browser.'
                : 'Elenco dei push generati con questo browser. Non richiede account.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline"><Link to={isReseller && brand ? `/b/${brand.slug}/p/new` : '/p/new'}>Nuovo push <ArrowRight className="h-4 w-4 ml-1.5" /></Link></Button>
          </div>
        </div>

        {/* Reseller stats & quick actions */}
        {isReseller && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card className="border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest"><Activity className="h-3.5 w-3.5" /> Push totali</div>
              <div className="mt-2 text-3xl font-extrabold text-slate-900">{stats?.total_pushes ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-1">Attivi: <span className="font-semibold text-emerald-700">{stats?.active_pushes ?? '—'}</span></div>
            </Card>
            <Card className="border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest"><Eye className="h-3.5 w-3.5" /> Visualizzazioni</div>
              <div className="mt-2 text-3xl font-extrabold text-slate-900">{stats?.total_views ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-1">Cumulative su tutti i push</div>
            </Card>
            <Card className="border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest"><Building2 className="h-3.5 w-3.5" /> Piano</div>
              <div className="mt-2 text-2xl font-extrabold text-slate-900 capitalize">{brand?.plan || 'free'}</div>
              <Link to="/account/subscription" className="text-xs font-semibold text-[#0d63b0] hover:underline mt-1 inline-block">Cambia piano →</Link>
            </Card>
            <Card className="border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold uppercase tracking-widest"><Globe className="h-3.5 w-3.5" /> Dominio</div>
              <div className="mt-2 text-sm font-mono text-slate-800 truncate">
                {brand?.custom_domain
                  ? (brand.domain_verified ? <span className="text-emerald-700">{brand.custom_domain}</span> : <span className="text-amber-700">{brand.custom_domain}</span>)
                  : <span className="text-slate-400">non configurato</span>}
              </div>
              <Link to="/account/domain" className="text-xs font-semibold text-[#0d63b0] hover:underline mt-1 inline-block">Configura DNS →</Link>
            </Card>
          </div>
        )}

        {/* Quick reseller actions */}
        {isReseller && (
          <div className="mb-8 grid md:grid-cols-3 gap-4">
            <QuickCard to="/account/brand" icon={Palette} title="Personalizza brand" desc="Logo, colori, testi hero" />
            <QuickCard to="/account/domain" icon={Globe} title="Dominio custom" desc="Configura CNAME e TXT" locked={brand?.plan !== 'pro'} />
            <QuickCard to="/account/subscription" icon={CreditCard} title="Abbonamento" desc="Cambia piano (mock)" />
          </div>
        )}

        {/* Filter tabs (reseller only) */}
        {isReseller && items && items.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Filter className="h-3 w-3" /> Filtro:</span>
            {[
              { id: 'all', label: `Tutti (${items.length})` },
              { id: 'active', label: `Attivi (${items.filter((p) => !p.expired).length})` },
              { id: 'expired', label: `Scaduti (${items.filter((p) => p.expired).length})` },
            ].map((f) => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`text-xs rounded-full px-3 py-1 border font-medium ${filter === f.id ? 'bg-[#0d63b0] text-white border-[#0d63b0]' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {items === null && (
          <div className="text-slate-500 text-sm">Caricamento...</div>
        )}

        {items && filtered && filtered.length === 0 && (
          <Card className="border border-dashed border-slate-300 p-12 text-center bg-slate-50/50">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 border border-blue-100 text-[#0d63b0]"><Inbox className="h-7 w-7" /></div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">{items.length === 0 ? 'Nessun push ancora' : 'Nessun push per questo filtro'}</h3>
            <p className="mt-1 text-sm text-slate-600">{items.length === 0 ? 'Crea il tuo primo push per condividere una password in modo sicuro.' : 'Prova a cambiare filtro per vedere altri push.'}</p>
            {items.length === 0 && (
              <Button asChild className="mt-5 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                <Link to={isReseller && brand ? `/b/${brand.slug}/p/new` : '/p/new'}>Crea push</Link>
              </Button>
            )}
          </Card>
        )}

        {items && filtered && filtered.length > 0 && (
          <div className="grid gap-4">
            {filtered.map((p) => {
              const K = kindMeta(p.kind);
              const expired = p.expired;
              return (
                <Card key={p.token} className={`border ${expired ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200 bg-white'} p-5 transition-shadow hover:shadow-md`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className={`inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-xs font-medium ${K.color}`}>
                      <K.icon className="h-3.5 w-3.5" /> {K.label}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-slate-700 truncate">
                        {p.brand_slug ? `${window.location.origin}/b/${p.brand_slug}/p/${p.token}` : `${window.location.origin}/p/${p.token}`}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {p.note ? <span className="italic text-slate-600">"{p.note}" · </span> : null}
                        Creato {timeAgo(p.created_at)}
                        {isReseller && <> · <span className="text-slate-600">{p.views_total || 0}</span> vis.</>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {expired ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 text-slate-600 px-2.5 py-1"><ShieldAlert className="h-3 w-3" /> Scaduto</span>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1"><Clock className="h-3 w-3" /> {p.days_left}g</span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1"><Eye className="h-3 w-3" /> {p.views_left}v</span>
                          {p.requires_passphrase && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1"><Lock className="h-3 w-3" /> Passphrase</span>}
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => copy(p.token, p.brand_slug)} disabled={expired}>
                        {copiedToken === p.token ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(p.token)} className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {!isReseller && (
          <Card className="mt-10 border border-slate-200 bg-blue-50/40 p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-[#0d63b0] flex items-center justify-center text-white"><Building2 className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">Vuoi una dashboard permanente?</div>
              <div className="text-sm text-slate-600">Diventa reseller: i tuoi push saranno legati al tuo account e visibili da qualsiasi browser.</div>
            </div>
            <Button asChild className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"><Link to="/register">Crea account gratuito</Link></Button>
          </Card>
        )}
      </div>
    </div>
  );
}

function QuickCard({ to, icon: Icon, title, desc, locked }) {
  return (
    <Link to={to} className="group">
      <Card className="border border-slate-200 p-5 hover:border-[#0d63b0]/60 hover:shadow-md transition-all">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-[#0d63b0]" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 flex items-center gap-1.5">{title} {locked && <Lock className="h-3.5 w-3.5 text-amber-600" />}</div>
            <div className="text-xs text-slate-500">{desc}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#0d63b0] group-hover:translate-x-0.5 transition-transform" />
        </div>
      </Card>
    </Link>
  );
}
